import { useContext } from 'react'

import {
  Button, Form, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import { AgencyProviderPackageModel } from '@models/types'

import useAgencyProviderPackage from '@hooks/useAgencyProviderPackage'
import SelectAgencies from './components/SelectAgencies'
import SelectPackages from './components/SelectPackages'
import useBulkCreateAgencyProviderPackagesModal from './hooks/useBulkCreateAgencyProviderPackagesModal'

const MODAL_KEY = 'BulkCreateAgencyProviderPackagesModal'

type ModalContextTypes = {
  modalData: {
    agencyProviderPackage: Partial<AgencyProviderPackageModel>,
  },
  topModalKey: string,
}

type ModalCallbacks = {
  closeModal: () => void,
  bulkCreateAgencyProviderPackages: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const BulkCreateAgencyProviderPackagesModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal, bulkCreateAgencyProviderPackages } = callbacks || {}

  const modalContext = useContext<ModalContextTypes>(ModalContext)
  const { modalData, topModalKey } = modalContext

  const isTopModal = topModalKey === MODAL_KEY

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const { agencyProviderPackage } = modalPayload

  const { agency_id, provider_package_id } = agencyProviderPackage

  const bulkCreateAgencyProviderPackagesPayload = useBulkCreateAgencyProviderPackagesModal(agencyProviderPackage)
  const {
    creating,
    entityParams,
    saveEnabled,
  } = bulkCreateAgencyProviderPackagesPayload

  const {
    callbacks: { bulkCreateAgencyProviderPackages: createFn },
  } = useAgencyProviderPackage(agencyProviderPackage)

  const bulkCreatePayload = {
    callbacks: {
      action: createFn,
      afterAction: closeModal,
    },
    entityParams,
    requestOptions: {
      include_agency_provider_package_agency: !!provider_package_id,
      include_agency_provider_package_provider_package: !!agency_id,
    },
    toastText: agency_id ? 'Packages Assigned' : 'Agencies Assigned',
  }

  const entityText = agency_id ? 'Packages' : 'Agencies'

  return (
    <SidebarModal callbacks={callbacks} modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title="Add" titleSecondLine={entityText} />

      <SidebarModal.Content>
        <Form>
          {!!provider_package_id && (
            <SelectAgencies
              bulkCreateAgencyProviderPackagesPayload={bulkCreateAgencyProviderPackagesPayload}
              isTopModal={isTopModal}
            />
          )}

          {!!agency_id && (
            <SelectPackages
              agencyId={agency_id}
              bulkCreateAgencyProviderPackagesPayload={bulkCreateAgencyProviderPackagesPayload}
              isTopModal={isTopModal}
            />
          )}
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating}
          onClick={() => bulkCreateAgencyProviderPackages(bulkCreatePayload)}
          size="large"
        >
          {saveEnabled ? `Add ${entityText}` : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <BulkCreateAgencyProviderPackagesModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
